import React, { useEffect, useState } from 'react'
import { Button, List, Row, Select, Spin } from 'antd'
import { DeleteFilled, PlusCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Modal } from '~/Components/Modal'
import { ScrollBar } from '~/Components/ScrollBar'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getIsDetailLoading,
  getIsRoleActionLoading,
  getModalVisibility,
  getServicePrincipalDetail,
  ModalName,
  ServicePrincipalActions,
} from '~/Store/ServicePrincipal'
import { RolesActions } from '~/Store/Role'
import { getListRoles } from '~/Store/Role/selectors'
import { getUserOrganizations } from '~/Store/User/selectors'

const { Option } = Select

export const ServicePrincipalDetail = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const visible = useAppSelector(
    getModalVisibility(ModalName.ServicePrincipalDetail),
  )
  const isLoading = useAppSelector(getIsDetailLoading)
  const isRoleActionLoading = useAppSelector(getIsRoleActionLoading)
  const detail = useAppSelector(getServicePrincipalDetail)
  const allRoles = useAppSelector(getListRoles)
  const organizations = useAppSelector(getUserOrganizations)
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>()

  useEffect(() => {
    dispatch(RolesActions.getRolesRequest('ServicePrincipal'))
  }, [])

  const selfClose = () => {
    dispatch(
      ServicePrincipalActions.setModalVisibility({
        name: ModalName.ServicePrincipalDetail,
        visible: false,
      }),
    )
  }

  const organizationName = detail?.organizationId
    ? organizations.find(o => o.guid === detail.organizationId)?.companyName ||
      detail.organizationId
    : '-'

  const assignedRoleIds = new Set(detail?.roles.map(r => r.roleId))
  const assignableRoles = (allRoles || []).filter(
    r => !assignedRoleIds.has(r.id),
  )

  const addRoleClick = () => {
    if (!detail || !selectedRoleId) return
    dispatch(
      ServicePrincipalActions.assignRole({
        servicePrincipalId: detail.id,
        roleId: selectedRoleId,
      }),
    )
    setSelectedRoleId(undefined)
  }

  const removeRoleClick = (roleId: string) => {
    if (!detail) return
    dispatch(
      ServicePrincipalActions.removeRole({
        servicePrincipalId: detail.id,
        roleId,
      }),
    )
  }

  return (
    <Modal
      visible={visible}
      loadingData={isLoading}
      title={t('admin.servicePrincipalInfo')}
      onCancel={selfClose}
      footer={null}
    >
      {detail && (
        <div>
          <div style={{ fontWeight: 'bolder', fontSize: '16px' }}>
            {detail.name}
          </div>
          <div style={{ marginTop: '4px', color: '#888' }}>
            {t('admin.spOrganization')}: {organizationName}
          </div>
          <div style={{ marginTop: '20px' }}>
            <Row align="middle" style={{ marginBottom: '10px' }}>
              <span style={{ flex: 1, fontWeight: 'bold' }}>
                {t('admin.spRoles')}
              </span>
            </Row>
            <Row style={{ marginBottom: '10px' }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                placeholder={t('admin.spSelectRole')}
                style={{ flex: 1, marginRight: '10px' }}
                value={selectedRoleId}
                onChange={setSelectedRoleId}
              >
                {assignableRoles.map(r => (
                  <Option key={r.id} value={r.id}>
                    {r.name}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                loading={isRoleActionLoading}
                disabled={!selectedRoleId}
                onClick={addRoleClick}
              >
                {t('admin.spAssignRole')}
              </Button>
            </Row>
            <Spin spinning={isRoleActionLoading}>
              <ScrollBar style={{ maxHeight: 'calc(100vh - 420px)' }}>
                <List
                  dataSource={detail.roles}
                  locale={{ emptyText: t('admin.spNoRoles') }}
                  renderItem={role => (
                    <List.Item
                      key={role.roleId}
                      actions={[
                        <DeleteFilled
                          key="remove"
                          style={{ color: 'red' }}
                          onClick={() => removeRoleClick(role.roleId)}
                        />,
                      ]}
                    >
                      {role.roleName}
                    </List.Item>
                  )}
                />
              </ScrollBar>
            </Spin>
          </div>
        </div>
      )}
    </Modal>
  )
}
